import { Header, SpaceBetween } from "@amzn/awsui-components-react";
import CustomCheckbox from "../CustomCheckbox/CustomCheckbox";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import LoadingContent from "common/component/LoadingContent/LoadingContent";

const getStringPath: string = "common.component.checkBoxFilter";

/**
 * Represents a node in the checkbox filter tree structure
 *
 * @interface
 * @property {string} id - Unique identifier for the checkbox node
 * @property {string} name - Display name of the checkbox node
 * @property {CheckboxNode} [parent] - Parent node in the tree structure
 * @property {CheckboxNode[]} [children] - Array of child nodes
 * @property {boolean} [selected] - Selection state of the checkbox
 */
export interface CheckboxNode {
    id: string;
    name: string;
    parent?: CheckboxNode;
    children?: CheckboxNode[];
    selected?: boolean;
}

/**
 * Props for the CheckboxFilter component
 *
 * @interface
 * @property {string} name - The name or label of the checkbox filter section
 * @property {boolean} [isLoading] - Loading state indicator
 * @property {CheckboxNode[]} checkBoxStructure - Array of checkbox nodes to render
 * @property {React.Dispatch<React.SetStateAction<CheckboxNode[]>>} setCheckBoxStructure - Function to update the checkbox structure
 * @property {string} [testId] - Optional test ID for the component
 * @property {string} [description] - Optional description for the checkbox filter section
 */
export interface CheckboxFilterProps {
    name: string;
    isLoading?: boolean;
    checkBoxStructure: CheckboxNode[];
    setCheckBoxStructure: React.Dispatch<React.SetStateAction<CheckboxNode[]>>;
    testId?: string;
    description?: string;
    touchpoint: string;
}

/**
 * Renders a multiple checkbox filter section with hierarchical checkbox structure
 *
 * @param {CheckboxFilterProps} props - The props for the CheckboxFilter component
 * @return {JSX.Element} - The rendered CheckboxFilter component
 */
export default function CheckboxFilter(props: CheckboxFilterProps) {
    if (props.isLoading) {
        return <LoadingContent loadingMessage={getString(`${getStringPath}.loadingFilters`)} />;
    }

    return (
        <SpaceBetween size="s" data-testid={props.testId}>
            <Header variant="h3" description={props.description}>
                {props.name}
            </Header>
            {props.checkBoxStructure.map((child) => (
                <CustomCheckbox
                    key={child.id}
                    checkBox={child}
                    checkBoxStructure={props.checkBoxStructure}
                    setCheckBoxStructure={props.setCheckBoxStructure}
                    touchpoint={props.touchpoint}
                />
            ))}
            {props.checkBoxStructure.length === 0 && <>{getString(`${getStringPath}.noFiltersFound`)}</>}
        </SpaceBetween>
    );
}
